import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Newspaper, ExternalLink, Quote } from "lucide-react";
import { useSite, usePublic } from "../../lib/site";
import { fmtDate, stripHtml } from "../../lib/i18n";
import { lazyImgs } from "../../lib/html";
import { Seo, breadcrumbLd } from "../../components/site/Seo";
import { ShareButtons } from "../../components/site/Cards";
import { Img } from "../../components/site/Common";
import { pressHref, PressCard } from "../../components/site/PressGrid";
import NotFound, { Loading } from "./NotFound";

export function PressDetail() {
  const { slug } = useParams();
  const { lang, t, site } = useSite();
  const { data: p, isLoading, isError } = usePublic(["press-item", slug], `/public/press/${slug}`);
  const { data: all = [] } = usePublic(["press"], "/public/press");
  if (isLoading) return <Loading />;
  if (isError || !p) return <NotFound />;
  const title = t(p.title);
  const summary = t(p.summary);
  const desc = t(p.seo?.description) || summary || stripHtml(t(p.body)).slice(0, 155);
  const hasBody = !!stripHtml(t(p.body));
  const others = all.filter((x) => x.slug !== p.slug).slice(0, 3);
  const backLabel = lang === "tr" ? "Basın Arşivi" : "Press Archive";
  const gotoSource = lang === "tr" ? "Haberi orijinal kaynakta oku" : "Read on source";
  const summaryLabel = lang === "tr" ? "Haberden" : "From the article";
  const sourceLabel = lang === "tr" ? "Kaynak" : "Source";
  const articleLd = {
    "@context": "https://schema.org", "@type": "NewsArticle",
    headline: title, datePublished: p.published_at, dateModified: p.updated_at || p.published_at,
    ...(p.cover ? { image: [p.cover] } : {}),
    author: { "@type": "Person", name: site.profile?.name || "Olgu Tansuğ" },
    ...(p.source_name ? { publisher: { "@type": "Organization", name: p.source_name } } : {}),
    ...(p.source_url ? { isBasedOn: p.source_url } : {}),
  };

  return (
    <article data-testid="press-detail" className="pb-24">
      <Seo title={t(p.seo?.title) || title} description={desc} image={p.seo?.image || p.cover || `/api/public/og/press/${p.slug}.png`} type="article"
        jsonLd={[articleLd, breadcrumbLd([{ name: backLabel, path: pressHref(lang) }, { name: title, path: pressHref(lang, p.slug) }])]} />

      {/* Back link */}
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14">
        <Link to={pressHref(lang)} className="inline-flex items-center gap-2 text-sm text-s-muted hover:text-s-primary transition-colors group" data-testid="press-back-link">
          <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
          {backLabel}
        </Link>
      </div>

      {/* Header: badge · title · meta */}
      <header className="max-w-4xl mx-auto px-5 sm:px-8 pt-6 sm:pt-8 pb-10 rise">
        {p.source_name && (
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-s-primary/40 bg-s-primary/5 text-[11px] font-semibold uppercase tracking-[0.18em] text-s-primary mb-6" data-testid="press-detail-source">
            <Newspaper size={13} />{p.source_name}
          </span>
        )}
        <h1
          className="text-[1.65rem] sm:text-4xl lg:text-[2.6rem] font-bold tracking-tight leading-[1.18]"
          style={{ fontFamily: "'Outfit','Plus Jakarta Sans',system-ui,-apple-system,sans-serif", fontFeatureSettings: "'kern','liga','calt'" }}
          data-testid="page-title"
        >
          {title}
        </h1>
        <div className="mt-7 flex flex-wrap items-center justify-between gap-4 text-sm text-s-muted">
          <span className="inline-flex items-center gap-1.5"><Calendar size={14} />{fmtDate(p.published_at, lang)}</span>
          {p.source_url && (
            <a href={p.source_url} target="_blank" rel="noopener noreferrer" data-testid="press-source-link"
              className="inline-flex items-center gap-2 rounded-full bg-s-primary text-s-onprimary px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity shadow-sm">
              {gotoSource} <ExternalLink size={15} />
            </a>
          )}
        </div>
      </header>

      {/* Cover */}
      {p.cover ? (
        <div className="max-w-5xl mx-auto px-5 sm:px-8 mb-12">
          <Img src={p.cover} alt={title} loading="eager" className="w-full aspect-[16/9] object-cover rounded-2xl border border-site-border shadow-md" />
        </div>
      ) : (
        <div className="max-w-5xl mx-auto px-5 sm:px-8 mb-12">
          <div className="w-full aspect-[16/7] rounded-2xl border border-site-border flex items-center justify-center bg-gradient-to-br from-s-primary/5 via-transparent to-s-primary/10 relative overflow-hidden">
            <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 25% 20%, currentColor 1px, transparent 1px), radial-gradient(circle at 70% 70%, currentColor 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
            <div className="relative text-center">
              <Newspaper size={56} strokeWidth={1.2} className="mx-auto text-s-primary/70 mb-3" />
              {p.source_name && <p className="text-xs uppercase tracking-[0.3em] font-semibold text-s-muted">{p.source_name}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        {hasBody ? (
          <div className="prose-site" data-testid="press-body" dangerouslySetInnerHTML={{ __html: lazyImgs(t(p.body)) }} />
        ) : summary ? (
          <figure className="rounded-2xl border border-site-border bg-[color:var(--s-surface,transparent)] p-6 sm:p-8" data-testid="press-summary">
            <div className="flex items-start gap-3 text-s-primary/70 mb-3">
              <Quote size={28} strokeWidth={1.6} className="shrink-0" />
              <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-s-muted mt-1">{summaryLabel}</span>
            </div>
            <blockquote className="text-lg sm:text-xl leading-[1.7] text-s-text italic font-light">
              {summary}
            </blockquote>
            {p.source_url && (
              <figcaption className="mt-5 pt-4 border-t border-site-border text-xs text-s-muted flex items-center justify-between gap-2 flex-wrap">
                <span>— {sourceLabel}: <span className="text-s-primary font-medium">{p.source_name || p.source_url}</span></span>
                <a href={p.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-s-primary hover:underline">
                  {gotoSource} <ExternalLink size={11} />
                </a>
              </figcaption>
            )}
          </figure>
        ) : null}

        <div className="mt-10 pt-8 border-t border-site-border">
          <ShareButtons title={title} testid="press-share-button" kind="press" imageUrl={p.cover} shareUrl={`${window.location.origin}/api/public/share/press/${p.slug}?v=${encodeURIComponent((p.updated_at || p.published_at || "").replace(/[^0-9]/g, "").slice(0, 12) || "1")}`} />
        </div>
      </div>

      {others.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-20">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8">{lang === "tr" ? "Diğer Basın Haberleri" : "More Press"}</h2>
          <div className="grid sm:grid-cols-3 gap-6 lg:gap-8">{others.map((o) => <PressCard key={o.id} p={o} />)}</div>
        </section>
      )}
    </article>
  );
}
